import React from 'react';
import { Stack, TablePagination } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-pro';
import { ICustomerExportOperation, CUSTOMER_OPERATIONS_LIMIT } from 'components/customers/export/models';
import { MuiGrid } from 'lib/MuiGrid/MuiGrid';
import { GridStorageName } from 'lib/MuiGrid/StateController';
import { CustomerExportedDataActionCell } from 'components/customers/export/tables/CustomerExportedDataActionCell';
import { OperationStatusCell } from 'lib/MuiGrid/OperationStatusCell';
import { OperationStatus } from 'components/operation/models/OperationModel';
import { formatDateTime } from 'lib/timeHelpers';

interface ICustomerExportedDataTableProps {
    operations: ICustomerExportOperation[];
    onClick: (item: ICustomerExportOperation) => void;
    page: number;
    count: number;
    loading: boolean;
    onPageChange: (page: number) => void;
}

export const CustomerExportedDataTable: React.FC<ICustomerExportedDataTableProps> = ({
    operations,
    onClick,
    page,
    count,
    loading,
    onPageChange
}) => {
    const columns = React.useMemo<GridColDef[]>(
        () => [
            {
                field: 'createdAt',
                headerName: 'Date',
                width: 180,
                sortable: false,
                valueFormatter: formatDateTime
            },
            {
                field: 'status',
                headerName: 'State',
                width: 120,
                sortable: false,
                renderCell: OperationStatusCell
            },
            {
                field: 'message',
                headerName: 'Message',
                flex: 1,
                sortable: false,
                valueGetter: (_value: unknown, row: ICustomerExportOperation) => {
                    if (row.status === OperationStatus.ERROR) {
                        return row.error?.message ?? '';
                    }
                    return row.result?.message ?? '';
                }
            },
            {
                field: 'action',
                headerName: '',
                width: 80,
                sortable: false,
                renderCell: (params: GridRenderCellParams<ICustomerExportOperation>) => (
                    <CustomerExportedDataActionCell operation={params.row} />
                )
            }
        ],
        []
    );

    const getRowId = React.useCallback((item: ICustomerExportOperation) => item._id, []);

    const handleRowClick = React.useCallback(
        (params: GridRowParams<ICustomerExportOperation>) => {
            onClick(params.row);
        },
        [onClick]
    );

    const handlePageChange = React.useCallback(
        (_: unknown, newPage: number) => {
            onPageChange(newPage);
        },
        [onPageChange]
    );

    const Pagination = React.useCallback(
        () => (
            <TablePagination
                count={count}
                onPageChange={handlePageChange}
                page={page}
                rowsPerPage={CUSTOMER_OPERATIONS_LIMIT}
                rowsPerPageOptions={[CUSTOMER_OPERATIONS_LIMIT]}
                component="div"
                disabled={loading}
            />
        ),
        [count, handlePageChange, page, loading]
    );

    const NoRowsOverlay = React.useCallback(
        () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
                No exports found
            </Stack>
        ),
        []
    );

    return (
        <MuiGrid
            rows={operations}
            columns={columns}
            getRowId={getRowId}
            onRowClick={handleRowClick}
            hideFooter={false}
            pagination
            paginationMode="server"
            rowCount={count}
            loading={loading}
            disableRowSelectionOnClick
            enableRowClick
            disableColumnFilter
            storageName={GridStorageName.CustomerExports}
            slots={{
                pagination: Pagination,
                noRowsOverlay: NoRowsOverlay
            }}
        />
    );
};
